<template>
  <Modal
    :modal-types="[DELETE_CHART_MODAL]"
    font-awesome-icon-class="fas fa-fw fa-trash-alt"
    modal-title="Delete Chart"
  >
    <form
      class="delete-form"
      @submit.prevent="handleDeleteChartFormSubmit()"
    >
      <p class="delete-chart-message">
        Are you sure you'd like to delete your saved chart?
        <br />
        <br />
        <span class="delete-chart-message-saved-chart-name" v-sanitize-html="nameOfChartToBeDeleted"></span>
        <br />
        <br />
        <span class="delete-chart-permanent-message">This action cannot be&nbsp;undone.</span>
      </p>
      <button
        type="submit"
        class="sc3-button sc3-button-red"
        :disabled="isDeletingChart"
      >
        <i v-show="!isDeletingChart" class="fas fa-fw fa-trash-alt"></i>
        <i v-show="isDeletingChart" class="fad fa-fw fa-spinner-third fa-spin"></i>
        &nbsp;Delete Saved Chart
      </button>
      <button
        type="button"
        class="sc3-button-link"
        @click="closeModal()"
      >
        Cancel
      </button>
    </form>
  </Modal>
</template>

<script lang="ts" setup>
  import { ref, computed, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useLayoutStore } from '@/stores/layoutStore';
  import { useSavedChartsStore } from '@/stores/savedChartsStore';
  import { useActionResultMessages } from '@/composables/useActionResultMessages';
  import { useModals } from '@/composables/useModals';
  import { deleteChart } from '@/composables/deleteChart';
  import { INVALID_CHART_RECID, isValidChartRecid } from '@/composables/isValidChartRecid';
  import { refreshChartLists } from '@/composables/refreshChartLists';
  import { loadChartsForChartList } from '@/composables/loadChartsForChartList';
  import { getChart } from '@/composables/getChart';
  import { clearRecidsOfChartAndLoadSymbolOnlyChartBeingViewed } from '@/composables/clearRecidsOfChartAndLoadSymbolOnlyChartBeingViewed';
  import { areObjectsEqual } from '@/utils/areObjectsEqual';
  import Modal from '@/components/modals/Modal.vue';

  const layoutStore = useLayoutStore();
  const savedChartsStore = useSavedChartsStore();
  const { modalType } = storeToRefs(layoutStore);
  const { recidOfChartBeingDeletedInModalView } = storeToRefs(savedChartsStore);
  const {
    addGreenActionResultMessage,
    addRedActionResultMessage
  } = useActionResultMessages();
  const { DELETE_CHART_MODAL, closeModal } = useModals();
  const isDeletingChart = ref(false);
  let lastModalOpenedTime = -1;
  let lastRecidOfChartBeingDeletedInModalViewChangedTime = -1;
  const chartToBeDeleted = computed(() => {
    if (!isValidChartRecid(recidOfChartBeingDeletedInModalView.value)) {
      return null;
    }

    for (const chartList of savedChartsStore.chartLists) {
      for (const chart of chartList.charts) {
        if (chart.recid === recidOfChartBeingDeletedInModalView.value) {
          return chart;
        }
      }
    }

    return null;
  });
  const nameOfChartToBeDeleted = computed(() => {
    return chartToBeDeleted.value ? chartToBeDeleted.value.name : '';
  });

  interface DeleteChartModalState {
    modalType: string;
    lastModalOpenedTime: number;
    recidOfChartBeingDeletedInModalView: number;
    lastRecidOfChartBeingDeletedInModalViewChangedTime: number;
  };

  async function handleDeleteChartFormSubmit() {
    if (
      isDeletingChart.value ||
      modalType.value !== DELETE_CHART_MODAL
    ) {
      return;
    }

    isDeletingChart.value = true;
    const modalState = getModalState();
    const chartRecid = recidOfChartBeingDeletedInModalView.value;

    if (!isValidChartRecid(chartRecid)) {
      window.sc3WorkbenchLogger.error(`Unable to delete chart from DeleteChartModal - chartRecid is invalid: ${chartRecid}.`);
      closeModal();
      addRedActionResultMessage('Failed to delete chart.');
      return;
    }

    const chart = await getChart(chartRecid, true);

    if (hasModalStateChanged(modalState)) {
      return;
    }

    if (!chart) {
      window.sc3WorkbenchLogger.error(`Unable to delete chart from DeleteChartModal - no chart found with recid: ${chartRecid}.`);
      closeModal();
      addRedActionResultMessage('Failed to delete chart.');
      return;
    }

    const chartList = savedChartsStore.chartLists.find(cl => cl.listNum === chart.chartListListNum);

    if (!chartList) {
      window.sc3WorkbenchLogger.error(`Unable to delete chart from DeleteChartModal - no ChartList found with listNum: ${chart.chartListListNum}.`);
      closeModal();
      addRedActionResultMessage('Failed to delete chart.');
      return;
    }

    if (chartList.isReadonly) {
      window.sc3WorkbenchLogger.error(`Unable to delete chart from DeleteChartModal - ChartList with listNum: ${chart.chartListListNum} is readonly.`);
      closeModal();
      addRedActionResultMessage('Failed to delete chart.');
      return;
    }

    const chartListRecid = chartList.recid;

    try {
      await deleteChart(chartRecid.toString());

      savedChartsStore.$patch((state) => {
        for (const savedChartList of state.chartLists) {
          savedChartList.charts = savedChartList.charts.filter(c => c.recid !== chartRecid);
        }

        if (state.recidOfChartBeingPreviewedInChartLibraryListView === chartRecid) {
          state.recidOfChartBeingPreviewedInChartLibraryListView = INVALID_CHART_RECID;
        }

        state.recidOfChartBeingDeletedInModalView = INVALID_CHART_RECID;
        state.recidOfChartBeingEditedInModalView = INVALID_CHART_RECID;
      });

      clearRecidsOfChartAndLoadSymbolOnlyChartBeingViewed(chartRecid);

      addGreenActionResultMessage('Success! Your chart has been deleted!');
      closeModal();

      return;
    } catch (e) {
      window.sc3WorkbenchLogger.error(`Error occurred while deleting chart with recid: ${chartRecid}.`, e);
    }

    if (hasModalStateChanged(modalState)) {
      return;
    }

    addRedActionResultMessage('Failed to delete chart.');
    closeModal();

    await refreshChartLists();
    await loadChartsForChartList(chartListRecid, undefined, true);
  }

  function getModalState(): DeleteChartModalState {
    return {
      modalType: modalType.value,
      lastModalOpenedTime,
      recidOfChartBeingDeletedInModalView: recidOfChartBeingDeletedInModalView.value,
      lastRecidOfChartBeingDeletedInModalViewChangedTime
    };
  }

  function hasModalStateChanged(modalState: DeleteChartModalState) {
    const currentModalState = getModalState();

    return !areObjectsEqual(modalState, currentModalState);
  }

  async function handleDeleteChartModalOpened() {
    await getChart(recidOfChartBeingDeletedInModalView.value, true);
  }

  function handleDeleteChartModalClosed(isReInitializingModal: boolean) {
    isDeletingChart.value = false;

    if (!isReInitializingModal) {
      savedChartsStore.$patch({ recidOfChartBeingDeletedInModalView: INVALID_CHART_RECID });
    }
  }

  async function handleRecidOfChartBeingDeletedInModalViewChanged() {
    lastRecidOfChartBeingDeletedInModalViewChangedTime = Date.now();

    handleDeleteChartModalClosed(true);

    if (modalType.value !== DELETE_CHART_MODAL) {
      return;
    }

    await handleDeleteChartModalOpened();
  }

  async function handleModalTypeChanged() {
    if (modalType.value !== DELETE_CHART_MODAL) {
      handleDeleteChartModalClosed(false);
      return;
    }

    lastModalOpenedTime = Date.now();

    await handleDeleteChartModalOpened();
  }

  watch(recidOfChartBeingDeletedInModalView, handleRecidOfChartBeingDeletedInModalViewChanged);
  watch(modalType, handleModalTypeChanged);
</script>

<style scoped>
  .delete-form {
    margin: 0px;
    padding: 0px;
    text-align: center;
  }

  .delete-chart-message {
    margin: 0px 0px 15px 0px;
    padding: 0px;
    font-size: 16px;
    font-weight: 500;
    color: #212529;
    line-height: 1.6;
  }

  .delete-chart-message-saved-chart-name {
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-weight: 700;
  }

  .delete-chart-permanent-message {
    font-weight: 700;
  }
</style>
